import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../redux/store';

export interface IImportRecord {
    id: string;
    rowNumber: number;
    status: 'pending' | 'created' | 'updated' | 'skipped' | 'failed';
    error: string | null;
    data: {
        index: number;
        companyName: string;
        addressType: string;
        fullAddress: string;
        [key: string]: any;
    };
}

export interface IImportProgress {
    total: number;
    processed: number;
    created: number;
    updated: number;
    skipped: number;
    failed: number;
    percentage: number;
}

/**
 * Tracks one address-book import.
 *
 * The record list arrives with the upload response, so rows render before the first event. Progress
 * events then carry only status deltas, which are merged by id; the completion event carries every
 * row's final status and overwrites, so a delta lost to a backgrounded tab still settles correctly.
 *
 * The socket is WAL's, not the portal API's — the portal's connection already points there.
 */
export const useAddressImportSocket = (jobId: string | null, initialRecords: IImportRecord[]) => {
    const { socketIo, isSocketConnected } = useSelector((state: RootState) => state.basic);

    const [records, setRecords] = useState<IImportRecord[]>(initialRecords);
    const [progress, setProgress] = useState<IImportProgress | null>(null);
    const [isComplete, setIsComplete] = useState(false);
    const [summary, setSummary] = useState<any>(null);

    useEffect(() => {
        setRecords(initialRecords);
        setProgress(null);
        setIsComplete(false);
        setSummary(null);
    }, [initialRecords]);

    useEffect(() => {
        if (!socketIo || !isSocketConnected || !jobId) {
            return;
        }

        const room = `cp_import_${jobId}`;

        socketIo.post('/subscribe/joinRoom', { roomNames: [room] }, (_body: any, jwr: any) => {
            if (jwr?.error) {
                console.error('useAddressImportSocket: could not join the progress room', jwr.error);
            }
        });

        const applyUpdates = (updates: { id: string; status: string; error: string | null }[] = []) => {
            if (updates.length === 0) {
                return;
            }

            const byId = new Map(updates.map((update) => [update.id, update]));

            setRecords((current) =>
                current.map((record) => {
                    const update = byId.get(record.id);

                    return update ? { ...record, status: update.status as IImportRecord['status'], error: update.error } : record;
                })
            );
        };

        const onProgress = (payload: any) => {
            if (payload?.jobId !== jobId) {
                return;
            }

            setProgress(payload.progress);
            applyUpdates(payload.updates);
        };

        const onComplete = (payload: any) => {
            if (payload?.jobId !== jobId) {
                return;
            }

            setProgress(payload.progress);
            applyUpdates(payload.records);
            setSummary(payload.summary);
            setIsComplete(true);
        };

        socketIo.on('import-progress', onProgress);
        socketIo.on('import-complete', onComplete);

        return () => {
            socketIo.off('import-progress', onProgress);
            socketIo.off('import-complete', onComplete);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socketIo, isSocketConnected, jobId]);

    return { records, progress, isComplete, summary };
};
